import {
  getFormattedPercent,
  getFormattedRevenue,
  getPeriodTitle,
} from 'legacy/lut/components/widgets/public/partner360/helpers/report'
import { FC, useMemo } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Flex } from 'core-components/library/flex'

import {
  getCurrYearBaseFields,
  getFieldWithSellType,
} from '../quarterlyViewMarketTable/helpers/fieldsNameHelper'
import { TRANSLATION_KEYS } from '../translations'

import { TotalItem } from './components/totalItem/TotalItem'
import { IQuarterlyViewTotalProps } from './models/iQuarterlyViewTotalProps'

import styles from './QuarterlyViewTotal.module.scss'

export const QuarterlyViewTotal: FC<IQuarterlyViewTotalProps> = ({
  totalData,
  sellType,
  selectedQuarters,
  currentYear,
  showPartnersCount = true,
  isExpectedExit = false,
}) => {
  const previousYear = useMemo(() => {
    return currentYear - 1
  }, [currentYear])

  const previousPeriodTitle = useMemo(() => {
    return getPeriodTitle(selectedQuarters, previousYear)
  }, [previousYear, selectedQuarters])

  const currentPeriodTitle = useMemo(() => {
    return getPeriodTitle(selectedQuarters, currentYear)
  }, [currentYear, selectedQuarters])

  const baseFields = getCurrYearBaseFields(isExpectedExit)

  return (
    <Flex className={styles.root}>
      <RenderWhen condition={showPartnersCount}>
        <TotalItem
          title={<Translation htmlAvailable text={TRANSLATION_KEYS.columns.partnersCount} />}
          value={totalData.partnersCount}
        />
      </RenderWhen>
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={TRANSLATION_KEYS.columns.prevYearSobBaseline}
            replaceParams={[
              {
                value: previousPeriodTitle,
                key: '{period}',
              },
            ]}
          />
        }
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType('prevYearSobActual', sellType)],
        })}
      />
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={TRANSLATION_KEYS.columns.currYearSobPlan}
            replaceParams={[
              {
                value: currentPeriodTitle,
                key: '{period}',
              },
            ]}
          />
        }
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType('currYearSobPlan', sellType)],
        })}
      />
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={
              isExpectedExit
                ? TRANSLATION_KEYS.columns.currYearSobExpectedExit
                : TRANSLATION_KEYS.columns.currYearSobActual
            }
            replaceParams={[
              {
                value: currentPeriodTitle,
                key: '{period}',
              },
            ]}
          />
        }
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType(baseFields.sob, sellType)],
        })}
      />
      <TotalItem
        title={<Translation htmlAvailable text={TRANSLATION_KEYS.columns.yoyPp} />}
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType(baseFields.yoyPP, sellType)],
          ending: 'pp',
          hasValueSign: true,
        })}
      />
      <TotalItem
        title={<Translation htmlAvailable text={TRANSLATION_KEYS.columns.vsPlan} />}
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType(baseFields.vsPlan, sellType)],
          ending: 'pp',
          hasValueSign: true,
        })}
      />
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={TRANSLATION_KEYS.columns.currYearSalesPlan}
            replaceParams={[
              {
                value: currentPeriodTitle,
                key: '{period}',
              },
              {
                value: sellType,
                key: '{netNdp}',
              },
            ]}
          />
        }
        value={getFormattedRevenue(
          totalData[getFieldWithSellType('currYearPlansRevenue', sellType)]
        )}
      />
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={
              isExpectedExit
                ? TRANSLATION_KEYS.columns.currYearExpectedExit
                : TRANSLATION_KEYS.columns.currYearActuals
            }
            replaceParams={[
              {
                value: currentPeriodTitle,
                key: '{period}',
              },
              {
                value: sellType,
                key: '{netNdp}',
              },
            ]}
          />
        }
        value={getFormattedRevenue(totalData[getFieldWithSellType(baseFields.revenue, sellType)])}
      />
      <TotalItem
        title={
          <Translation
            htmlAvailable
            text={TRANSLATION_KEYS.columns.salesPerf}
            replaceParams={[
              {
                value: currentPeriodTitle,
                key: '{period}',
              },
            ]}
          />
        }
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType(baseFields.salesPerformance, sellType)],
        })}
      />
      <TotalItem
        title={<Translation htmlAvailable text={TRANSLATION_KEYS.columns.yoyGrowth} />}
        value={getFormattedPercent({
          percent: totalData[getFieldWithSellType(baseFields.yoyGrowth, sellType)],
          hasValueSign: true,
        })}
      />
    </Flex>
  )
}
